import { Stack, Typography } from '@mui/material';
import { fetchAuthSession } from 'aws-amplify/auth';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CompanyAvatarInverted,
  CompanyItem,
  CompanyWrapper,
} from '@/console/features/switch-company/components/switch-company-menu/switch-company-menu.styled';
import { TSwitchCompanyMenuProps } from '@/console/features/switch-company/components/switch-company-menu/switch-company-menu.types';
import { CompanyName } from '@/console/features/switch-company/switch-company.styled';
import { Checkbox } from '@/shared/components/ui/checkbox/checkbox';
import { FullscreenLoader } from '@/shared/components/ui/fullscreen-loader/fullscreen-loader';
import { Tooltip } from '@/shared/components/ui/tooltip/tooltip';
import { useTenant } from '@/shared/features/auth/context/tenant-context';
import {
  useReadTenantQuery,
  useSelectTenantMutation,
} from '@/shared/redux/api/ident/ident-api';
import { BaseTenantFragment } from '@/shared/types/types.generated';
import { getAvatarLabel } from '@/shared/utils/avatar';
import { showToastError } from '@/shared/utils/toast';

export const SwitchCompanyMenu: React.FC<TSwitchCompanyMenuProps> = ({
  onClose,
  companies,
}) => {
  const { t } = useTranslation();
  const { data } = useReadTenantQuery();
  const [selectTenant] = useSelectTenantMutation();
  const [isSwitching, setIsSwitching] = useState<boolean>(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(
    null,
  );
  const currentTenantId = data?.readTenant.tenantId;
  const { disconnect } = useTenant();

  const handleSelectTenant = async (tenant: BaseTenantFragment) => {
    const { tenantId, companyName } = tenant;
    setSelectedCompanyName(companyName);
    if (tenantId === currentTenantId) {
      onClose();
      return;
    }

    setIsSwitching(true);
    try {
      disconnect();

      await selectTenant({ tenantId }).unwrap();
      await fetchAuthSession({ forceRefresh: true });
      window.location.href = '/';
    } catch {
      setIsSwitching(false);
      showToastError(t('error:tenant_switch_failed'));
    }
  };

  return (
    <>
      <FullscreenLoader
        display={isSwitching}
        loaderText={t('rbac:switching_to', {
          companyName: selectedCompanyName,
        })}
      />
      <Stack spacing="1rem">
        <Typography variant="h7" color="textSecondary">
          {t('ui:switch_company')}
        </Typography>
        <Stack spacing="0.5rem" maxHeight="max(300px, 30vh)" overflow="auto">
          {companies.map((company, index) => (
            <CompanyItem
              key={`${index}__${company}`}
              onClick={() => handleSelectTenant(company)}
            >
              <CompanyWrapper>
                <CompanyAvatarInverted>
                  {getAvatarLabel(company.companyName)}
                </CompanyAvatarInverted>
                <Stack gap="2px">
                  <CompanyName>{company.companyName}</CompanyName>
                  <Tooltip
                    title={`${t('common:owner')}: ${company.owner.email}`}
                    noMaxWidth
                    disableInteractive
                  >
                    <Typography variant="p10" color="textSecondary">
                      {company.owner.firstName} {company.owner.lastName}
                    </Typography>
                  </Tooltip>
                </Stack>
              </CompanyWrapper>
              <Checkbox
                checked={company.tenantId === currentTenantId}
                size="large"
              />
            </CompanyItem>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
